/* eslint-disable */
import { useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useUserStore } from "../stores/user.store";
import { centralizedLogout, AUTH_CONSTANTS } from "../utils/auth";

/**
 * Hook to automatically logout user after a period of inactivity
 * Tracks user activity (mouse, keyboard, touch, scroll) and resets timeout
 *
 * @param timeoutMinutes - Inactivity timeout in minutes (default: 30)
 * @param enabled - Whether to enable session timeout (default: true)
 */
export const useSessionTimeout = (timeoutMinutes = 30, enabled = true) => {
	const { logout, isAuthenticated } = useAuth0();
	const { clearUser, userInfo } = useUserStore();
	const timeoutRef = useRef<NodeJS.Timeout>();
	const timeoutMs = timeoutMinutes * 60 * 1000;

	const handleTimeout = useCallback(async () => {
		console.log("[SessionTimeout] User inactive, logging out...");
		await centralizedLogout(clearUser, logout, "session_timeout");
	}, [clearUser, logout]);

	const resetTimeout = useCallback(() => {
		// Clear existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		// Set new timeout
		timeoutRef.current = setTimeout(handleTimeout, timeoutMs);
	}, [handleTimeout, timeoutMs]);

	useEffect(() => {
		// Only run if authenticated, enabled, and user is loaded
		if (!isAuthenticated || !enabled || !userInfo) {
			return;
		}

		// Events that indicate user activity
		const events = [
			"mousedown",
			"mousemove",
			"keydown",
			"scroll",
			"touchstart",
			"click",
		];

		// Add event listeners
		events.forEach((event) => {
			document.addEventListener(event, resetTimeout, { passive: true });
		});

		// Start initial timeout
		resetTimeout();

		// Cleanup
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			events.forEach((event) => {
				document.removeEventListener(event, resetTimeout);
			});
		};
	}, [isAuthenticated, enabled, userInfo, resetTimeout]);
};
